import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavigationStateService } from '../../../../../services/navigation-state.service';

@Component({
  selector: 'app-unified-nav',
  templateUrl: './unified-nav.component.html',
  styleUrls: ['./unified-nav.component.scss']
})
export class UnifiedNavComponent implements OnInit, OnDestroy {
  @Input() navigation: any[] = [];
  @Input() isCollapsed: boolean = false;
  @Input() isMobileMenuOpen: boolean = false;
  @Output() onToggleCollapse = new EventEmitter<void>();
  @Output() onMobileToggle = new EventEmitter<void>();

  // Navigation state
  public isMobile: boolean = false;
  public expandedItems: Set<string> = new Set();
  public openSubmenus: Set<string> = new Set();
  public activeItem: string = '';
  public currentRoute: string = '';
  public dropdownPosition: { top: number, left: number } = { top: 60, left: 70 };
  // Desktop collapsed state is now managed by NavigationStateService
  public get isDesktopCollapsed(): boolean {
    return this.navigationStateService.isDesktopCollapsed;
  }

  // Responsive breakpoints
  private readonly MOBILE_BREAKPOINT = 992;

  constructor(
    private router: Router,
    private navigationStateService: NavigationStateService
  ) {}

  ngOnInit() {
    // Ensure navigation is initialized
    if (!this.navigation) {
      this.navigation = [];
    }
    
    this.checkScreenSize();
    this.setupRouteListener();
    this.setupResizeListener();
    this.setupDropdownListener();
    
    // Apply desktop collapsed state to parent from service
    if (!this.isMobile && this.isDesktopCollapsed) {
      this.isCollapsed = true;
    }
    
    // Ensure collapsed state is preserved
    this.preserveCollapsedState();
    
    // Set initial active item based on current route (only if navigation has items)
    if (this.navigation && this.navigation.length > 0) {
      this.currentRoute = this.router.url;
      this.setActiveItem();
    }
  }

  ngOnDestroy() {
    // Cleanup listeners
  }

  // Desktop collapsed state management
  // Desktop collapsed state is now managed by NavigationStateService

  private preserveCollapsedState() {
    if (!this.isMobile) {
      // Ensure collapsed state is synchronized
      this.isCollapsed = this.isDesktopCollapsed;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < this.MOBILE_BREAKPOINT;
    
    // Close mobile menu when switching to desktop
    if (!wasMobile && this.isMobile) {
      this.isMobileMenuOpen = false;
    }
    
    // Close mobile menu when switching to mobile
    if (wasMobile && !this.isMobile) {
      this.isMobileMenuOpen = false;
    }
    
    // If switching from mobile to desktop, apply saved collapsed state from service
    if (wasMobile && !this.isMobile) {
      // Apply desktop collapsed state to parent from service
      this.isCollapsed = this.isDesktopCollapsed;
    }
  }

  private setupRouteListener() {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.url;
        
        // Always update active item on route change
        this.setActiveItem();
        
        // Close mobile menu on navigation
        if (this.isMobile) {
          this.isMobileMenuOpen = false;
        }
        
        // Preserve desktop collapsed state on route change
        if (!this.isMobile) {
          this.isCollapsed = this.isDesktopCollapsed;
        }
      });
  }

  private setupResizeListener() {
    window.addEventListener('resize', () => {
      this.checkScreenSize();
    });
  }

  private setupDropdownListener() {
    // Close dropdowns when clicking outside (but not on nested menu items)
    document.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking on navigation items, links, submenus, nested collapses, or nested submenus
      if (!target.closest('.nav-dropdown') && 
          !target.closest('.nav-link') && 
          !target.closest('.nav-icon') &&
          !target.closest('.nav-arrow') &&
          !target.closest('.pcoded-submenu') &&
          !target.closest('.pcoded-hasmenu') &&
          !target.closest('.nav-submenu') &&
          !target.closest('.nav-submenu-link') &&
          !target.closest('.nav-submenu-collapse-link') &&
          !target.closest('.nav-submenu-nested') &&
          !target.closest('.mobile-nav-submenu') &&
          !target.closest('.mobile-nav-submenu-link') &&
          !target.closest('.mobile-nav-submenu-collapse-link') &&
          !target.closest('.mobile-nav-submenu-nested')) {
        // Only clear top-level expanded items, keep nested ones
        const topLevelItems = Array.from(this.expandedItems);
        for (const itemId of topLevelItems) {
          // Check if this is a top-level item
          const item = this.navigation.find(i => i.id === itemId);
          if (item && item.type === 'collapse') {
            // Check if it's nested inside another submenu
            const itemElement = document.querySelector(`[data-item-id="${itemId}"]`);
            if (!itemElement || itemElement.closest('.nav-submenu') === null) {
              this.expandedItems.delete(itemId);
            }
          }
        }
      }
    });
  }

  private setActiveItem() {
    // Clear previous active items
    this.activeItem = null;
    
    // Safety check: ensure navigation exists and has items
    if (!this.navigation || !Array.isArray(this.navigation) || this.navigation.length === 0) {
      return;
    }
    
    // Normalize current route (remove trailing slash if exists)
    const normalizedRoute = this.currentRoute.replace(/\/$/, '') || '/';
    
    // Track which menus should be expanded (only those with active route)
    const menusToExpand = new Set<string>();
    
    // Find active item based on current route - support multilevel
    for (const item of this.navigation) {
      // Check top-level items
      if (item.type === 'item' && item.url) {
        const normalizedItemUrl = item.url.replace(/\/$/, '') || '/';
        if (normalizedItemUrl === normalizedRoute) {
          this.activeItem = item.id;
          // Close any expanded menus since this is a top-level item
          this.expandedItems.clear();
          this.openSubmenus.clear();
          return;
        }
      }
      
      // Check collapse items and their children
      if (item.type === 'collapse' && item.children) {
        const found = this.findActiveInChildren(item, normalizedRoute);
        if (found) {
          this.activeItem = found.itemId;
          
          // Expand ALL parent menus that contain the active route
          found.parentIds.forEach(id => {
            menusToExpand.add(id);
            this.expandedItems.add(id);
            if (this.isMobile && this.openSubmenus) {
              this.openSubmenus.add(id);
            }
          });
          
          return;
        }
      }
    }
    
    // If no active item found, still keep relevant menus open if they were manually opened
    // Don't clear expandedItems on route change - only expand menus with active routes
    
    // Preserve desktop collapsed state after setting active items
    if (!this.isMobile) {
      this.isCollapsed = this.isDesktopCollapsed;
    }
  }

  private findActiveInChildren(parent: any, route: string, parentIds: string[] = []): { itemId: string; parentIds: string[] } | null {
    if (!parent.children || parent.children.length === 0) {
      return null;
    }
    
    const currentParentIds = parent.id ? [...parentIds, parent.id] : parentIds;
    
    for (const child of parent.children) {
      // Check if this child is an item and matches the route
      if (child.type === 'item' && child.url) {
        const normalizedChildUrl = (child.url || '').replace(/\/$/, '') || '/';
        const normalizedRoute = (route || '').replace(/\/$/, '') || '/';
        if (normalizedChildUrl === normalizedRoute) {
          return { itemId: child.id, parentIds: currentParentIds };
        }
      }
      
      // If child is a nested collapse, recursively check
      if (child.type === 'collapse' && child.children) {
        const found = this.findActiveInChildren(child, route, currentParentIds);
        if (found) {
          return found;
        }
      }
    }
    
    return null;
  }

  // Navigation methods
  toggleCollapse() {
    if (this.isMobile) {
      this.toggleMobileMenu();
    } else {
      // Desktop collapse toggle using service
      this.navigationStateService.toggleDesktopCollapsed();
      this.isCollapsed = this.isDesktopCollapsed;
      this.onToggleCollapse.emit();
    }
  }

  toggleMobileMenu() {
    this.onMobileToggle.emit();
  }

  closeMobileMenu() {
    this.onMobileToggle.emit();
  }

  toggleSubmenu(itemId: string, event: Event, parentItem?: any) {
    event.preventDefault();
    event.stopPropagation();
    
    // Check if this is a nested menu (inside a parent submenu)
    const target = event.currentTarget as HTMLElement;
    const isNested = target.closest('.nav-submenu') !== null;
    
    // Find the clicked item in navigation to check if it's top-level
    const clickedItem = this.navigation.find(item => item.id === itemId);
    const isTopLevel = clickedItem && clickedItem.type === 'collapse' && !isNested;
    
    // If menu is already expanded, toggle it closed
    if (this.expandedItems.has(itemId)) {
      this.expandedItems.delete(itemId);
      return;
    }
    
    // If it's a top-level menu, close ALL other top-level menus first
    if (isTopLevel) {
      // Close all other top-level menus (regardless of active routes)
      for (const item of this.navigation) {
        if (item.type === 'collapse' && item.id !== itemId) {
          // Always close other top-level menus when opening a new one
          this.expandedItems.delete(item.id);
          // Also close any nested menus inside the closed parent
          if (item.children) {
            item.children.forEach((child: any) => {
              if (child.type === 'collapse') {
                this.expandedItems.delete(child.id);
              }
            });
          }
        }
      }
    } else {
      // For nested menus, close other siblings but keep parent open
      if (parentItem && parentItem.children) {
        const siblingIds = parentItem.children
          .filter((child: any) => child.type === 'collapse' && child.id !== itemId)
          .map((child: any) => child.id);
        siblingIds.forEach(id => this.expandedItems.delete(id));
      }
    }
    
    // Open the clicked menu
    this.expandedItems.add(itemId);
    
    // Calculate position based on clicked element (only for collapsed desktop)
    if (this.isCollapsed && !this.isMobile) {
      const rect = target.getBoundingClientRect();
      this.dropdownPosition = {
        top: rect.top,
        left: 70
      };
    }
  }

  onMenuItemClick(item: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    
    if (item.type === 'collapse' && item.children && item.children.length > 0) {
      // Don't navigate if it has children, just toggle submenu
      // Toggle: if already open, close it; otherwise, open it and close others
      this.toggleSubmenu(item.id, event, item);
    } else if (item.type === 'item' && item.url) {
      // For regular items, navigate and close mobile menu
      this.router.navigate([item.url]);
      this.closeMobileMenu();
    }
  }

  onNestedMenuClick(item: any, event: Event, parentItem?: any) {
    event.preventDefault();
    event.stopPropagation();
    
    // Handle nested collapse (level 2 menu)
    if (item.type === 'collapse' && item.children && item.children.length > 0) {
      // Toggle nested submenu but keep parent open
      // If already expanded, close it
      if (this.expandedItems.has(item.id)) {
        this.expandedItems.delete(item.id);
      } else {
        // Close other siblings in the same parent, but keep parent open
        if (parentItem && parentItem.children) {
          const siblingIds = parentItem.children
            .filter((child: any) => child.type === 'collapse' && child.id !== item.id)
            .map((child: any) => child.id);
          siblingIds.forEach(id => this.expandedItems.delete(id));
        }
        this.expandedItems.add(item.id);
      }
    } else if (item.type === 'item' && item.url) {
      this.router.navigate([item.url]);
      this.closeMobileMenu();
    }
  }

  onSubmenuItemClick(item: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    
    if (item.url) {
      this.router.navigate([item.url]);
      this.closeMobileMenu();
    }
  }

  // Utility methods
  isExpanded(itemId: string): boolean {
    return this.expandedItems.has(itemId);
  }

  hasChildren(item: any): boolean {
    return item.type === 'collapse' && item.children && item.children.length > 0;
  }

  isActive(item: any): boolean {
    if (item.type === 'item') {
      // Normalize routes for comparison
      const normalizedItemUrl = (item.url || '').replace(/\/$/, '') || '/';
      const normalizedCurrentRoute = this.currentRoute.replace(/\/$/, '') || '/';
      return normalizedItemUrl === normalizedCurrentRoute;
    }
    if (item.type === 'collapse' && item.children) {
      // Recursively check if any child or nested child matches the current route
      // Also check if the item itself is expanded (parent menu is open)
      return this.hasActiveChild(item, this.currentRoute);
    }
    return false;
  }
  
  isParentActive(item: any): boolean {
    // Check if this parent menu contains the active route (used for highlighting parent)
    if (item.type === 'collapse' && item.children) {
      return this.hasActiveChild(item, this.currentRoute);
    }
    return false;
  }

  private hasActiveChild(item: any, route: string): boolean {
    if (!item.children || item.children.length === 0) {
      return false;
    }
    
    // Normalize route for comparison
    const normalizedRoute = route.replace(/\/$/, '') || '/';
    
    // Check direct children
    for (const child of item.children) {
      if (child.type === 'item' && child.url) {
        const normalizedChildUrl = child.url.replace(/\/$/, '') || '/';
        if (normalizedChildUrl === normalizedRoute) {
          return true;
        }
      }
      // Recursively check nested collapse children
      if (child.type === 'collapse' && child.children) {
        if (this.hasActiveChild(child, route)) {
          return true;
        }
      }
    }
    
    return false;
  }

  getItemClasses(item: any): string {
    const classes = ['nav-item'];
    
    if (this.isActive(item)) {
      classes.push('active');
    }
    
    if (item.type === 'collapse' && this.hasChildren(item)) {
      classes.push('has-submenu');
    }
    
    if (this.isExpanded(item.id)) {
      classes.push('expanded');
    }
    
    return classes.join(' ');
  }
}
